import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Nav, NavItem } from 'reactstrap';
import { Link } from 'react-router-dom';

import Layout from '@/components/Layout/Layout';
import CardLayout from '@/components/Layout/CardLayout';
import BackButton from '@/components/ui/BackButton';
import TabContainer from '@/components/common/TabContainer';

import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import OffboardingProfileStep from './OffbordingSteps/OffboardingProfileStep';
import OffboardingDetailsStep from './OffbordingSteps/OffboardingDetailsStep';
import OffboardingCompleteStep from './OffbordingSteps/OffboardingCompleteStep';
import {
  IOffboardingFormInputs,
  IEmployeeUser,
} from '@/interfaces/offbording.model';
import { IUser } from '@/interfaces/user.model';

export interface EmployeeProfile extends IEmployeeUser {
  userdetail?: {
    fatherName?: string;
    motherName?: string;
    ecpName?: string;
    ecpPhone?: string;
  };
}

interface IOffboardingDetailResponse {
  status: boolean;
  message?: string;
  data: {
    id: string;
    OffBoardType: string;
    reason: string;
    joiningDate: string;
    lastWorkingDay: string;
    tenure: number;
    assetHandover: boolean;
    isCompletedProject: boolean;
    status: string;
    userId: string;
    handoverId: string;
    // employeeUser: IEmployeeUser;
    // handoverUser: IEmployeeUser;
    employeeUser: IUser; // now matches state type
    handoverUser: IUser;
  };
}

const AddEmployeeOffBoarding: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [activeTab, setActiveTab] = useState<number>(1);
  const [offboardingId, setOffboardingId] = useState<string>(id || '');
  const [employeeDetails, setEmployeeDetails] =
    useState<Partial<IOffboardingFormInputs> | null>(null);
  //   const [employeeData, setEmployeeData] = useState<IEmployeeUser | null>(null);
  const [employeeData, setEmployeeData] = useState<IUser | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchOffboarding = async () => {
        setLoading(true);
        try {
          const response = (await callApi(
            `${API_URL}${API_ENDPOINT.offbordingDetails}/${id}`,
            { method: 'GET' },
          )) as IOffboardingDetailResponse;

          if (response?.status) {
            const data = response.data;
            setOffboardingId(id);
            setUserId(data.userId);

            setEmployeeDetails({
              offBoardType: data.OffBoardType,
              reason: data.reason,
              joiningDate: data.joiningDate,
              lastWorkingDay: data.lastWorkingDay,
              handoverId: data.handoverId,
              assetHandover: data.assetHandover,
              isCompletedProject: data.isCompletedProject,
              status: data.status,
            });

            if (data.employeeUser) {
              setEmployeeData(data.employeeUser);
            }

            setActiveTab(data.status === 'Completed' ? 3 : 2);
          } else {
            showToastNotification(
              'danger',
              response?.message || 'Failed to load offboarding data',
            );
          }
        } catch {
          showToastNotification('danger', 'Error loading offboarding data');
        } finally {
          setLoading(false);
        }
      };
      fetchOffboarding();
    }
  }, [isEditMode, id]);

  const formSteps = [
    { tabId: 1, label: 'Profile' },
    { tabId: 2, label: 'Details' },
    { tabId: 3, label: 'Complete' },
  ];

  const handleNextStep = () => setActiveTab((prev) => prev + 1);
  const handleComplete = () => navigate(RoutesLink?.employees || '/');

  const isTabAccessible = (tabId: number) => {
    if (tabId === 1) return true;
    if (tabId === 2) return !!employeeData && !!userId;
    if (tabId === 3) return !!offboardingId;
    return false;
  };

  if (loading) {
    return (
      <Layout>
        <div className="content container-fluid">
          <div className="text-center">Loading offboarding data...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="content container-fluid">
        <BackButton label="Employee" isEdit={isEditMode} />
        <CardLayout cardStyle="mb-4">
          <div className="wizard-tabs">
            <Nav tabs className="nav nav-pills">
              {formSteps.map((step, index) => (
                <React.Fragment key={step.tabId}>
                  <NavItem>
                    <Link
                      to="#"
                      className={
                        activeTab === step.tabId
                          ? 'border-0 p-2 active nav-link'
                          : 'nav-link'
                      }
                      style={{
                        cursor: isTabAccessible(step.tabId)
                          ? 'pointer'
                          : 'not-allowed',
                      }}
                      onClick={(e) => {
                        if (isTabAccessible(step.tabId)) {
                          setActiveTab(step.tabId);
                        } else {
                          e.preventDefault();
                          showToastNotification(
                            'info',
                            'Please complete the previous step first',
                          );
                        }
                      }}
                    >
                      <h6>Step {step.tabId}</h6>
                      <h5>{step.label}</h5>
                    </Link>
                  </NavItem>
                  {index < formSteps.length - 1 && (
                    <li className="nav-item">
                      <div className="border-top-item" />
                    </li>
                  )}
                </React.Fragment>
              ))}
            </Nav>
          </div>
        </CardLayout>

        <CardLayout>
          <TabContainer activeTab={activeTab} tabId={1}>
            <OffboardingProfileStep
              setEmployeeData={setEmployeeData}
              employeeData={employeeData}
              setUserId={setUserId}
              nextStep={handleNextStep}
              isEditMode={isEditMode}
              initialUserId={userId}
              initialEmployeeData={employeeData}
            />
          </TabContainer>

          <TabContainer activeTab={activeTab} tabId={2}>
            {employeeData && userId ? (
              <OffboardingDetailsStep
                employeeData={employeeData}
                userId={userId}
                setOffboardingId={setOffboardingId}
                setEmployeeDetails={setEmployeeDetails}
                nextStep={handleNextStep}
                isEditMode={isEditMode}
                offboardingId={offboardingId}
                initialValues={employeeDetails || {}}
                isCompleted={employeeDetails?.status === 'Completed'}
              />
            ) : (
              <div className="alert alert-warning">
                Please select an employee in the Profile step first.
              </div>
            )}
          </TabContainer>

          <TabContainer activeTab={activeTab} tabId={3}>
            {offboardingId ? (
              <OffboardingCompleteStep
                offboardingId={offboardingId}
                employeeDetails={employeeDetails}
                onComplete={handleComplete}
                initialCheckboxes={{
                  assetHandover: employeeDetails?.assetHandover || false,
                  isCompletedProject:
                    employeeDetails?.isCompletedProject || false,
                }}
              />
            ) : (
              <div className="alert alert-warning">
                No offboarding record found. Please complete the Details step
                first.
              </div>
            )}
          </TabContainer>
        </CardLayout>
      </div>
    </Layout>
  );
};

export default AddEmployeeOffBoarding;
